import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Button, Card, message, Modal, Spin, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { DaoRentalPoolInfo, DaoRewardPoolsData } from 'Modules'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useAccount } from 'wagmi'

import { AppAdmin } from 'api/app-admin'
import ButtonCopy from 'component/button/button-copy'
import { DaoAssetStatusEnum } from 'constants/dao-voting'
import { FEATURE } from 'constants/feature'
import { SettingEnum } from 'constants/setting'
import { customErrorContract } from 'hooks/contract/use-contract-viem'
import { useStopRentalRewards } from 'hooks/dao-voting/use-stop-rental-rewards'
import { useStopStakingRewards } from 'hooks/dao-voting/use-stop-staking-rewards'
import { useAppSetting } from 'hooks/setting/use-app-setting'
import { usePermission } from 'hooks/use-permission'
import { onCloseModalProcessing, onOpenModalProcessing } from 'stores/modal.store'
import { logError } from 'utils/logger'

interface SystemActionsCardProps {
  /** tokenize (PTO project) id */
  tokenizeId: string
  /** tokenize.sellingVersion === ERC3643 */
  isErc3643?: boolean
}

const StatusText = ({ kind, label }: { kind: 'muted' | 'processing' | 'done'; label: string }) => {
  if (kind === 'processing') {
    return (
      <span className="dao-system-status processing">
        <Spin size="small" /> {label}
      </span>
    )
  }
  return <span className={`dao-system-status ${kind}`}>{label}</span>
}

/**
 * System Actions (SRS 5.8/5.9) - shown once the sale result is recorded.
 * Data comes from GET /admin/dao-voting/pto-projects/:id/reward-pools:
 * staking pool (MEY or USDC per poolKind) and the revenue-sharing rental pool.
 */
const SystemActionsCard = ({ tokenizeId, isErc3643 }: SystemActionsCardProps) => {
  const { address } = useAccount()
  const { canCRUD } = usePermission()
  const { getSetting } = useAppSetting()
  const canManage = canCRUD(FEATURE.DAO)
  const { stopStakingRewards } = useStopStakingRewards()
  const { stopRentalRewards } = useStopRentalRewards()

  const [data, setData] = useState<DaoRewardPoolsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [stopping, setStopping] = useState(false)
  /** rentalId of the pool whose stop tx is in flight */
  const [stoppingRentalId, setStoppingRentalId] = useState<string | null>(null)

  const getRewardPools = useCallback(async () => {
    try {
      const res = await AppAdmin.getDaoVotingRewardPools(tokenizeId)
      setData(res.data?.data || null)
    } catch (error) {
      logError('Error fetching reward pools: ', error)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [tokenizeId])

  useEffect(() => {
    getRewardPools()
  }, [getRewardPools])

  const stakingPool = data?.stakingPool
  const rentalPools = data?.revenueSharingRentalPools || []

  // PTO ERC-3643 has its own staking pool contract. Otherwise poolKind decides
  // which StakingNFT contract holds the collection:
  // MEY -> stake_nft_address_mey, USDC -> stake_nft_address
  const stakingPoolContract = isErc3643
    ? stakingPool?.stakingTokenize?.contractAddress
    : stakingPool?.poolKind === 'MEY'
      ? getSetting(SettingEnum.STAKE_NFT_ADDRESS_MEY)
      : getSetting(SettingEnum.STAKE_NFT_ADDRESS)

  const canStopStaking =
    canManage &&
    stakingPool?.status === 'CONFIRMED' &&
    !!stakingPoolContract &&
    (isErc3643 || !!data?.tokenize?.contractAddress)

  const handleStopStaking = () => {
    if (!address) {
      message.error('Please connect wallet!')
      return
    }
    Modal.confirm({
      title: 'Stop Staking Rewards',
      content: `Stop staking rewards for ${data?.tokenize?.fullName} on the ${
        stakingPool?.poolKind || ''
      } staking pool? Holders will no longer accrue staking rewards for this PTO.`,
      okText: 'Stop Rewards',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setStopping(true)
          onOpenModalProcessing()
          await stopStakingRewards(address, {
            poolAddress: stakingPoolContract || '',
            collectionAddress: data?.tokenize?.contractAddress || '',
            isErc3643,
          })
          message.success('Staking rewards stopped successfully')
          getRewardPools()
        } catch (error) {
          logError('Error stopping staking rewards: ', error)
          message.error(customErrorContract(error) || 'Failed to stop staking rewards')
        } finally {
          onCloseModalProcessing()
          setStopping(false)
        }
      },
    })
  }

  const fmtPeriod = (ts?: number | null) =>
    ts != null ? dayjs(ts * 1000).format('YYYY-MM-DD HH:mm') : '--'

  const shortAddress = (value: string) => `${value.slice(0, 6)}...${value.slice(-4)}`

  const handleStopRental = (pool: DaoRentalPoolInfo) => {
    if (!address) {
      message.error('Please connect wallet!')
      return
    }
    Modal.confirm({
      title: 'Stop Rental Rewards',
      content: `Stop rental revenue-sharing rewards for the period ${fmtPeriod(
        pool.properties?.startDate
      )} → ${fmtPeriod(
        pool.properties?.endDate
      )}? The pool's end time will be set to now and holders will no longer accrue rental rewards.`,
      okText: 'Stop Rewards',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setStoppingRentalId(pool.rentalId)
          onOpenModalProcessing()
          await stopRentalRewards(address, pool.contractAddress || '')
          message.success('Rental rewards stopped successfully')
          getRewardPools()
        } catch (error) {
          logError('Error stopping rental rewards: ', error)
          message.error(customErrorContract(error) || 'Failed to stop rental rewards')
        } finally {
          onCloseModalProcessing()
          setStoppingRentalId(null)
        }
      },
    })
  }

  // PENDING = waiting for BE confirm, CONFIRMED = running, END = stopped
  const rentalPoolStatus = (pool: DaoRentalPoolInfo) =>
    !pool.status ? (
      <StatusText kind="muted" label="Not started" />
    ) : pool.status === 'PENDING' ? (
      <StatusText kind="processing" label="Processing" />
    ) : pool.status === 'END' ? (
      <StatusText kind="done" label="Stopped" />
    ) : (
      <StatusText kind="muted" label="Active" />
    )

  /** left side + right-side status per row */
  const stakingRow: { action: ReactNode; status: ReactNode } = {
    action: canStopStaking ? (
      <Button className="dao-view-detail-btn" loading={stopping} onClick={handleStopStaking}>
        Stop Staking Rewards
      </Button>
    ) : (
      <span className="dao-system-action-label">Stop Staking Rewards</span>
    ),
    status:
      !stakingPool || !stakingPool.status ? (
        <StatusText kind="muted" label="Not started" />
      ) : stakingPool.status === 'PENDING' ? (
        <StatusText kind="processing" label="Processing" />
      ) : stakingPool.status === 'END' ? (
        <StatusText kind="done" label="Stopped" />
      ) : (
        // CONFIRMED = running, stop available
        <StatusText kind="muted" label="Active" />
      ),
  }

  // SALE_FAILED: system actions only surface once the refund pool has been
  // created - every other status keeps the current behavior
  if (!loading && data?.assetStatus === DaoAssetStatusEnum.SALE_FAILED && !data?.daoPool) {
    return null
  }

  return (
    <>
      <div className="dao-section-title">
        System Actions{' '}
        <Tooltip title="Reward emissions tied to this PTO. Stop them once the sale result is recorded so holders no longer accrue rewards.">
          <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }} />
        </Tooltip>
      </div>
      <Card className="dao-system-actions">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 12 }}>
            <Spin />
          </div>
        ) : (
          <>
            <div className="dao-system-action-row with-divider">
              {stakingRow.action}
              {stakingRow.status}
            </div>

            {/* one sub-row per rental period pool (mockup) */}
            <div className="dao-system-action-row">
              <span className="dao-system-action-label">Stop Rental Rewards</span>
              {rentalPools.length ? (
                <span className="dao-system-status muted">Per rental pool</span>
              ) : (
                <StatusText kind="muted" label="Not started" />
              )}
            </div>
            {rentalPools.map((pool) => (
              <div
                key={pool.rentalId}
                className="dao-rental-pool-item"
                style={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 16,
                  border: '1px solid #f0f0f0',
                  borderRadius: 10,
                  padding: '10px 14px',
                  marginBottom: 10,
                }}
              >
                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#111', whiteSpace: 'nowrap' }}>
                    {fmtPeriod(pool.properties?.startDate)} → {fmtPeriod(pool.properties?.endDate)}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      fontSize: 12,
                      color: 'rgba(0,0,0,0.45)',
                      marginTop: 2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Contract address:{' '}
                    {pool.contractAddress ? (
                      <>
                        <span style={{ fontFamily: 'monospace' }}>
                          {shortAddress(pool.contractAddress)}
                        </span>
                        <ButtonCopy
                          title="Copy"
                          text={pool.contractAddress}
                          icon={<CopyOutlined />}
                        />
                      </>
                    ) : (
                      'Not deployed'
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    marginLeft: 'auto',
                    flex: '0 0 auto',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {rentalPoolStatus(pool)}
                  {canManage && pool.status === 'CONFIRMED' && pool.contractAddress ? (
                    <Button
                      size="small"
                      className="dao-view-detail-btn"
                      loading={stoppingRentalId === pool.rentalId}
                      onClick={() => handleStopRental(pool)}
                    >
                      Stop
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </>
        )}
      </Card>
    </>
  )
}

export default SystemActionsCard
